import type { DroneAttack, TooltipNewsItem } from '@/types';

// ---------------------------------------------------------------------------
// Drone Attack Engine — OSINT-based scenarios
// SNS/news-sourced: Telegram war channels, Twitter OSINT accounts
// Real-world events: Ukraine FPV/Lancet, Houthi Shahed, Gaza, Myanmar
// ---------------------------------------------------------------------------

interface DroneScenario {
  id: string;
  type: DroneAttack['type'];
  originLon: number;
  originLat: number;
  targetLon: number;
  targetLat: number;
  firedBy: string;
  targetName: string;
  flightTimeMs: number;
  cycleOffsetMs: number;
  cooldownMs: number;
  relatedNews: TooltipNewsItem[];
  snsSources: string[];
}

const DRONE_SCENARIOS: DroneScenario[] = [
  // Ukraine — FPV drone attacks on Russian armor
  {
    id: 'drone-ua-fpv-1',
    type: 'fpv',
    originLon: 37.5, originLat: 47.8,
    targetLon: 38.1, targetLat: 47.5,
    firedBy: 'Ukraine (GUR)',
    targetName: 'Russian Armor Column (Zaporizhzhia)',
    flightTimeMs: 8_000,
    cycleOffsetMs: 0,
    cooldownMs: 12_000,
    relatedNews: [
      { title: 'Ukraine FPV drones destroy 3 Russian IFVs near Robotyne', source: 'DeepState MAP', publishedAt: '2026-03-07T08:12:00Z' },
      { title: 'GUR confirms 47 Russian vehicles destroyed this week via FPV', source: 'Ukrainska Pravda', publishedAt: '2026-03-06T14:00:00Z' },
    ],
    snsSources: ['@UAWeapons (Twitter)', 'WarMonitor (Telegram)', 'DeepStateMAP'],
  },
  // Ukraine — Lancet loitering munition
  {
    id: 'drone-ua-lancet-1',
    type: 'loitering',
    originLon: 36.0, originLat: 48.5,
    targetLon: 37.8, targetLat: 48.0,
    firedBy: 'Russia (VDV)',
    targetName: 'Ukrainian Artillery Position (Kharkiv)',
    flightTimeMs: 18_000,
    cycleOffsetMs: 5_000,
    cooldownMs: 22_000,
    relatedNews: [
      { title: 'Russian Lancet drones target Ukrainian M777 howitzers', source: 'RYbar (Telegram)', publishedAt: '2026-03-07T06:30:00Z' },
      { title: 'Ukraine reports 2 HIMARS launchers damaged by Lancet strikes', source: 'ISW', publishedAt: '2026-03-06T20:00:00Z' },
    ],
    snsSources: ['RYbar (Telegram)', '@WarshipCam (Twitter)', 'Militaryland.net'],
  },
  // Russia — Shahed-136 strike on Ukraine infrastructure
  {
    id: 'drone-ru-shahed-1',
    type: 'shahed',
    originLon: 33.5, originLat: 46.2,
    targetLon: 30.5, targetLat: 50.4,
    firedBy: 'Russia (via Crimea)',
    targetName: 'Kyiv Energy Grid',
    flightTimeMs: 35_000,
    cycleOffsetMs: 10_000,
    cooldownMs: 60_000,
    relatedNews: [
      { title: 'Russia launches 28 Shahed drones targeting Ukrainian power infrastructure', source: 'Ukrainian Air Force', publishedAt: '2026-03-05T03:14:00Z' },
      { title: 'Ukraine intercepts 19 of 28 Shaheds, Kyiv oblast power restored', source: 'Ukrinform', publishedAt: '2026-03-05T08:00:00Z' },
    ],
    snsSources: ['@KyivIndependent (Twitter)', 'Ukraine Air Force (Telegram)', 'NEXTA (Telegram)'],
  },
  // Houthi — Shahed attacking Red Sea shipping
  {
    id: 'drone-houthi-1',
    type: 'shahed',
    originLon: 43.5, originLat: 15.4,
    targetLon: 43.0, targetLat: 13.5,
    firedBy: 'Houthis (Ansar Allah)',
    targetName: 'Commercial Vessel (Red Sea)',
    flightTimeMs: 22_000,
    cycleOffsetMs: 15_000,
    cooldownMs: 40_000,
    relatedNews: [
      { title: 'Houthi drone strikes container ship MSC Unity in Red Sea', source: 'UKMTO', publishedAt: '2026-03-07T10:30:00Z' },
      { title: 'USN USS Carney intercepts 3 Houthi drones over Red Sea', source: 'CENTCOM', publishedAt: '2026-03-06T22:00:00Z' },
    ],
    snsSources: ['@HouthiMedia (Telegram)', 'UKMTO Advisory', '@BNONews (Twitter)'],
  },
  // Houthi — naval drone (Aqeel) targeting warships
  {
    id: 'drone-houthi-naval-1',
    type: 'naval',
    originLon: 42.8, originLat: 14.8,
    targetLon: 43.5, targetLat: 12.5,
    firedBy: 'Houthis (Ansar Allah)',
    targetName: 'USS Gravely (DDG-107)',
    flightTimeMs: 28_000,
    cycleOffsetMs: 25_000,
    cooldownMs: 50_000,
    relatedNews: [
      { title: 'Houthi naval drone intercepted near USS Gravely in Gulf of Aden', source: 'US 5th Fleet', publishedAt: '2026-03-04T16:00:00Z' },
    ],
    snsSources: ['@US5thFleet (Twitter)', 'OSINT Defender (Telegram)'],
  },
  // Iran — Shahed supply corridor to Russia (tracked via Caspian)
  {
    id: 'drone-ir-supply-1',
    type: 'shahed',
    originLon: 51.5, originLat: 36.8,
    targetLon: 49.8, targetLat: 40.4,
    firedBy: 'Iran (IRGC → Russia transfer)',
    targetName: 'Caspian Transfer Point (Astrakhan)',
    flightTimeMs: 55_000,
    cycleOffsetMs: 30_000,
    cooldownMs: 90_000,
    relatedNews: [
      { title: 'Iran ships new Shahed-238 drones to Russia via Caspian route', source: 'Reuters', publishedAt: '2026-02-28T12:00:00Z' },
      { title: 'EU sanctions Iranian drone component manufacturers', source: 'EU Council', publishedAt: '2026-03-01T09:00:00Z' },
    ],
    snsSources: ['@IranIntl (Twitter)', 'Middle East Eye'],
  },
  // Israel — surveillance drone over Lebanon
  {
    id: 'drone-il-surv-1',
    type: 'surveillance',
    originLon: 35.0, originLat: 32.8,
    targetLon: 35.5, targetLat: 33.8,
    firedBy: 'Israel (IDF)',
    targetName: 'Southern Lebanon Surveillance',
    flightTimeMs: 30_000,
    cycleOffsetMs: 8_000,
    cooldownMs: 20_000,
    relatedNews: [
      { title: 'IDF Hermes 900 drones conduct reconnaissance over Hezbollah strongholds', source: 'Times of Israel', publishedAt: '2026-03-07T07:00:00Z' },
    ],
    snsSources: ['@IDF (Twitter)', 'OsintCal (Telegram)'],
  },
  // Ukraine — Switchblade 600 strike on Russian command post
  {
    id: 'drone-ua-sw-1',
    type: 'switchblade',
    originLon: 33.5, originLat: 47.0,
    targetLon: 34.5, targetLat: 46.5,
    firedBy: 'Ukraine (Marine Corps)',
    targetName: 'Russian Command Post (Kherson)',
    flightTimeMs: 14_000,
    cycleOffsetMs: 20_000,
    cooldownMs: 25_000,
    relatedNews: [
      { title: 'Ukraine uses US-supplied Switchblade 600 against Russian BMP in Kherson', source: 'Defense News', publishedAt: '2026-03-06T11:00:00Z' },
    ],
    snsSources: ['@UAArmedForces (Telegram)', 'MilitaryLand.net'],
  },
  // Myanmar junta — drone strikes on resistance forces
  {
    id: 'drone-mm-1',
    type: 'loitering',
    originLon: 96.2, originLat: 21.5,
    targetLon: 96.8, targetLat: 20.8,
    firedBy: 'Myanmar Junta (SAC)',
    targetName: 'PDF Resistance Position (Sagaing)',
    flightTimeMs: 20_000,
    cycleOffsetMs: 35_000,
    cooldownMs: 35_000,
    relatedNews: [
      { title: 'Myanmar military deploys Chinese-supplied drones against Karen resistance', source: 'Myanmar Now', publishedAt: '2026-03-05T09:00:00Z' },
    ],
    snsSources: ['@MyanmarWitness (Twitter)', 'DVB (Telegram)'],
  },
  // Russia — FPV drone swarm (Ukraine counterpart)
  {
    id: 'drone-ru-fpv-1',
    type: 'fpv',
    originLon: 37.2, originLat: 48.3,
    targetLon: 36.5, targetLat: 48.8,
    firedBy: 'Russia (DPR forces)',
    targetName: 'Ukrainian Infantry Position (Avdiivka)',
    flightTimeMs: 9_000,
    cycleOffsetMs: 45_000,
    cooldownMs: 15_000,
    relatedNews: [
      { title: 'Russia deploys FPV drone swarms targeting Ukrainian defensive lines near Avdiivka', source: 'ISW', publishedAt: '2026-03-07T05:00:00Z' },
    ],
    snsSources: ['RYbar (Telegram)', 'Rybar OSINT', '@WarMapper (Twitter)'],
  },
];

// ---------------------------------------------------------------------------
// Animation engine (same pattern as missile-engine)
// ---------------------------------------------------------------------------

export function tickDrones(nowMs: number): DroneAttack[] {
  return DRONE_SCENARIOS.map((s): DroneAttack => {
    const period = s.flightTimeMs + s.cooldownMs;
    const elapsed = (nowMs + s.cycleOffsetMs) % period;
    const progress = Math.min(elapsed / s.flightTimeMs, 1);

    let status: DroneAttack['status'];
    if (elapsed >= s.flightTimeMs + (s.cooldownMs * 0.15)) {
      status = 'cooldown';
    } else if (elapsed >= s.flightTimeMs) {
      // 15% of cooldown = impact flash
      status = 'impact';
    } else {
      status = 'inbound';
    }

    // Drones fly low — use very small height (0.1 vs missile 2.5)
    const t = progress;
    const sinArc = Math.sin(t * Math.PI) * 0.8; // small arc for low-altitude
    const currentLon = s.originLon + (s.targetLon - s.originLon) * t;
    const currentLat = s.originLat + (s.targetLat - s.originLat) * t + sinArc * 0.3;

    return {
      id: s.id,
      type: s.type,
      originLon: s.originLon,
      originLat: s.originLat,
      targetLon: s.targetLon,
      targetLat: s.targetLat,
      firedBy: s.firedBy,
      targetName: s.targetName,
      flightTimeMs: s.flightTimeMs,
      cycleOffsetMs: s.cycleOffsetMs,
      cooldownMs: s.cooldownMs,
      status,
      progress,
      currentLon,
      currentLat,
      relatedNews: s.relatedNews,
      snsSources: s.snsSources,
    };
  });
}

export const DRONE_TYPE_LABELS: Record<DroneAttack['type'], string> = {
  fpv:         'FPV Kamikaze',
  loitering:   'Loitering Munition',
  surveillance:'Surveillance UAV',
  naval:       'Naval Drone',
  shahed:      'Shahed-136/238',
  switchblade: 'Switchblade',
};

export const DRONE_TYPE_COLORS: Record<DroneAttack['type'], string> = {
  fpv:         'text-yellow-400',
  loitering:   'text-orange-400',
  surveillance:'text-sky-400',
  naval:       'text-blue-400',
  shahed:      'text-red-400',
  switchblade: 'text-emerald-400',
};
